import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, TrendingUp, Mail, Settings, FileText, ClipboardList, Tag,
  CreditCard, DollarSign, Package, Shield, Globe, Phone, RefreshCw,
  LogOut, Loader2, BarChart3,
} from "lucide-react";
import Logo from "@/components/Logo";
import LeadsTab from "@/components/admin/LeadsTab";
import ChatLogsTab from "@/components/admin/ChatLogsTab";
import WebhooksTab from "@/components/admin/WebhooksTab";
import OverviewTab from "@/components/admin/OverviewTab";
import CachedReportsTab from "@/components/admin/CachedReportsTab";
import AuditDetailsTab from "@/components/admin/AuditDetailsTab";
import CouponsTab from "@/components/admin/CouponsTab";
import UserReportsTab from "@/components/admin/UserReportsTab";
import PackagesTab from "@/components/admin/PackagesTab";
import UsersTab from "@/components/admin/UsersTab";
import ConversionsTab from "@/components/admin/ConversionsTab";
import BillingTab from "@/components/admin/BillingTab";
import TransactionsTab from "@/components/admin/TransactionsTab";
import EmailCenterTab from "@/components/admin/EmailCenterTab";
import SettingsTab from "@/components/admin/SettingsTab";
import FunnelTab from "@/components/admin/FunnelTab";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Sidebar navigation structure ─── */
const SIDEBAR_SECTIONS = [
  {
    label: "MAIN",
    items: [
      { id: "overview", label: "All Leads", icon: Users },
      { id: "crm-leads", label: "CRM Leads", icon: Phone },
      { id: "reports", label: "Cached Reports", icon: FileText },
      { id: "audits", label: "Audit Details", icon: ClipboardList },
      { id: "coupons", label: "Coupons", icon: Tag },
    ],
  },
  {
    label: "MANAGEMENT",
    items: [
      { id: "user-reports", label: "User Reports", icon: FileText },
      { id: "packages", label: "Packages", icon: Package },
      { id: "users", label: "Users", icon: Shield },
      { id: "emails", label: "Email Center", icon: Mail },
      { id: "chat-logs", label: "Chat Logs", icon: ClipboardList },
    ],
  },
  {
    label: "ANALYTICS",
    items: [
      { id: "funnel", label: "Audit Funnel", icon: BarChart3 },
      { id: "conversions", label: "Conversions", icon: TrendingUp },
      { id: "billing", label: "Billing", icon: CreditCard },
      { id: "transactions", label: "Transactions", icon: DollarSign },
    ],
  },
  {
    label: "CONFIG",
    items: [
      { id: "settings", label: "General Settings", icon: Settings },
      { id: "webhooks", label: "Webhooks", icon: Globe },
    ],
  },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const adminToken = localStorage.getItem("adminToken");

  // ── Auth check ──
  useEffect(() => {
    if (!adminToken) { navigate("/admin"); return; }
    axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(() => setLoading(false))
      .catch(() => { localStorage.removeItem("adminToken"); navigate("/admin"); });
  }, [adminToken, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    toast.success("Logged out");
    navigate("/admin");
  };

  const tabLabel = SIDEBAR_SECTIONS.flatMap(s => s.items).find(i => i.id === activeTab)?.label || "Dashboard";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <Loader2 className="w-12 h-12 text-[#3B5BDB] animate-spin" />
      </div>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <OverviewTab adminToken={adminToken} />;
      case "crm-leads": return <LeadsTab adminToken={adminToken} />;
      case "reports": return <CachedReportsTab adminToken={adminToken} />;
      case "audits": return <AuditDetailsTab adminToken={adminToken} />;
      case "coupons": return <CouponsTab adminToken={adminToken} />;
      case "user-reports": return <UserReportsTab adminToken={adminToken} />;
      case "packages": return <PackagesTab adminToken={adminToken} />;
      case "users": return <UsersTab adminToken={adminToken} />;
      case "emails": return <EmailCenterTab adminToken={adminToken} />;
      case "chat-logs": return <ChatLogsTab adminToken={adminToken} />;
      case "funnel": return <FunnelTab adminToken={adminToken} />;
      case "conversions": return <ConversionsTab adminToken={adminToken} />;
      case "billing": return <BillingTab adminToken={adminToken} />;
      case "transactions": return <TransactionsTab adminToken={adminToken} />;
      case "webhooks": return <WebhooksTab adminToken={adminToken} />;
      case "settings": return <SettingsTab adminToken={adminToken} />;
      default: return <OverviewTab adminToken={adminToken} />;
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]" data-testid="admin-dashboard">
      {/* ══ SIDEBAR ══ */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 min-h-screen" style={{ background: "#0f172a" }} data-testid="admin-sidebar">
        <div className="p-5">
          <Logo size="small" theme="dark" />
        </div>
        <nav className="flex-1 px-3 space-y-4 mt-2 overflow-y-auto">
          {SIDEBAR_SECTIONS.map(section => (
            <div key={section.label}>
              <p className="text-[10px] font-bold tracking-widest text-white/40 px-3 mb-1">{section.label}</p>
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <button key={item.id} onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === item.id
                        ? "bg-white/15 text-white"
                        : "text-white/60 hover:bg-white/10 hover:text-white"
                    }`}
                    data-testid={`sidebar-${item.id}`}>
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/10 transition-all"
            data-testid="logout-btn">
            <LogOut className="w-4 h-4" />Logout
          </button>
        </div>
      </aside>

      {/* ══ MAIN ══ */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]" data-testid="admin-header">
          <div className="px-6 flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-[#111827]">{tabLabel}</h1>
              <Badge className="bg-[#EEF2FF] text-[#3B5BDB] border-[#3B5BDB]/20">Admin</Badge>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#6B7280] hidden sm:block">{localStorage.getItem("adminUsername") || "Admin"}</span>
              <Button onClick={() => navigate("/")} size="sm" className="bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white" data-testid="back-to-app-btn">Back to App</Button>
            </div>
          </div>
          {/* Mobile tab bar */}
          <div className="md:hidden flex gap-2 px-4 pb-2 overflow-x-auto">
            {SIDEBAR_SECTIONS.flatMap(s => s.items).map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  activeTab === item.id ? "bg-[#3B5BDB] text-white" : "bg-slate-100 text-slate-600"
                }`}>{item.label}</button>
            ))}
          </div>
        </header>

        <main className="flex-1 p-6">
          {renderTab()}
        </main>
      </div>
    </div>
  );
}
